import React, { useEffect, useState, useMemo } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { MetricStrip } from '../common/MetricStrip';
import { EmptyState } from '../common/EmptyState';
import { Tag } from '../common/Tag';
import { Modal } from '../common/Modal';
import { fmtDate } from '../../utils/helpers';
import { studyManagementServices } from '../../APIServices/studyManagementServices';
import Pagination from '../../hooks/Pagination';
import { usersManagementServices } from '../../APIServices/usersManagementServices';

function StudiesListPage({ onOpenStudy }) {
  const { upsert } = useData();
  const toast = useToast();
  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [countryList, setCountryList] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [studyList, setStudyList] = useState([]);
  const [studyCount, setStudyCount] = useState(0);
  const [deletedStudyList, setDeletedStudyList] = useState([]);
  const [deletedStudyCount, setDeletedStudyCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState('');
  const [studyData, setStudyData] = useState(null);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [confirmUpdate, setConfirmUpdate] = useState(false);

  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const totalPage = Math.ceil(studyCount / itemsPerPage);

  const [deletedPageNumber, setDeletedPageNumber] = useState(1);
  const [deletedItemsPerPage, setDeletedItemsPerPage] = useState(10);
  const deletedTotalPage = Math.ceil(deletedStudyCount / deletedItemsPerPage);

  const [updateForm, setUpdateForm] = useState({
    studyCode: '',
    studyLabel: '',
    phaseType: '',
    protocolTitle: '',
    moleculeProduct: '',
    indication: '',
    design: '',
    doseForm: '',
    cro: '',
    country: '',
    plannedStart: '',
    plannedEnd: ''
  });

  const [addForm, setAddForm] = useState({
    studyCode: '',
    studyLabel: '',
    phaseType: '',
    protocolTitle: '',
    moleculeProduct: '',
    indication: '',
    design: '',
    doseForm: '',
    cro: '',
    country: '',
    plannedStart: '',
    plannedEnd: ''
  });

  const getStudyManagementData = async () => {
    setLoading(true);
    try {
      const data = await studyManagementServices.getStudyManagement(
        statusFilter,
        debouncedFilter,
        pageNumber,
        itemsPerPage
      );
      setStudyList(data?.data || []);
      setStudyCount(data?.count || 0);
    } catch (error) {
      setStudyList([]);
      setStudyCount(0);
      toast('Unable to load study management data.', 'warn');
    } finally {
      setLoading(false);
    }
  };

  const getDeletedStudyManagementData = async () => {
    try {
      const data = await studyManagementServices.getStudyManagement(
        'deleted',
        '',
        deletedPageNumber,
        deletedItemsPerPage
      );
      setDeletedStudyList(data?.data || []);
      setDeletedStudyCount(data?.count || 0);
    } catch (error) {
      setDeletedStudyList([]);
      setDeletedStudyCount(0);
      toast('Unable to load deleted study list.', 'warn');
    }
  };

  const closeModal = () => {
    setModalOpen('');
    setStudyData(null);
    setButtonLoader(false);
    setConfirmUpdate(false);
  };

  const openAddModal = () => {
    setAddForm({
      studyCode: '',
      studyLabel: '',
      phaseType: '',
      protocolTitle: '',
      moleculeProduct: '',
      indication: '',
      design: '',
      doseForm: '',
      cro: '',
      country: '',
      plannedStart: '',
      plannedEnd: ''
    });
    setModalOpen('add');
  };

  const addStudyManagementData = async () => {
    if (!addForm?.studyCode || !addForm?.protocolTitle) {
      toast('Study code and protocol title are required.', 'warn');
      return;
    }

    setButtonLoader(true);
    const data = await studyManagementServices.addNewStudyManagement(addForm);
    if (data?.statusCode === 200) {
      toast('Study created successfully.', 'ok');
      closeModal();
      getStudyManagementData();
    }
    setButtonLoader(false);
  };

  const openUpdateModal = (row) => {
    setStudyData(row);
    setConfirmUpdate(false);
    setUpdateForm({
      studyCode: row.studyCode || '',
      studyLabel: row.studyLabel || '',
      phaseType: row.phaseType || '',
      protocolTitle: row.protocolTitle || '',
      moleculeProduct: row.moleculeProduct || '',
      indication: row.indication || '',
      design: row.design || '',
      doseForm: row.doseForm || '',
      cro: row.cro || '',
      country: row.country || '',
      plannedStart: row.plannedStart || '',
      plannedEnd: row.plannedEnd || ''
    });
    setModalOpen('update');
  };

  const updateStudyManagementData = async () => {
    if (!studyData?.studyId || !updateForm.studyCode || !updateForm.protocolTitle) {
      toast('Study code and protocol title are required.', 'warn');
      return;
    }

    setButtonLoader(true);
    const payload = {
      studyId: studyData.studyId,
      ...updateForm
    };

    const data = await studyManagementServices.updateStudyManagement(payload);
    if (data?.statusCode === 200) {
      toast('Study updated successfully.', 'ok');
      closeModal();
      getStudyManagementData();
    }
    setButtonLoader(false);
  };

  const softDeleteStudyManagement = async () => {
    if (!studyData?._id && !studyData?.studyId) return;
    setButtonLoader(true);
    const data = await studyManagementServices.softDeleteRestoreStudy({
      id: studyData._id,
      studyId: studyData.studyId,
      type: 'delete'
    });
    if (data?.statusCode === 200) {
      toast('Study moved to deleted history.', 'ok');
      closeModal();
      getStudyManagementData();
      getDeletedStudyManagementData();
    }
    setButtonLoader(false);
  };

  const restoreStudyManagement = async (id, studyId) => {
    const shouldRestore = window.confirm('Are you sure you want to restore this study?');
    if (!shouldRestore) return;

    const data = await studyManagementServices.softDeleteRestoreStudy({
      id,
      studyId,
      type: 'restore'
    });
    if (data?.statusCode === 200) {
      toast('Study restored successfully.', 'ok');
      getStudyManagementData();
      getDeletedStudyManagementData();
    }
  };

  const deleteStudyManagementData = async (id, studyId) => {
    const shouldDelete = window.confirm('Are you sure you want to permanently delete this study?');
    if (!shouldDelete) return;

    const data = await studyManagementServices.deleteStudyManagement({ id, studyId });
    if (data?.statusCode === 200) {
      toast('Study permanently deleted.', 'ok');
      getDeletedStudyManagementData();
    }
  };

  const getCountries = async () => {
    let data = await usersManagementServices.getCountries();
    setCountryList(data || []);
  };

  useEffect(() => {
    getCountries();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPageNumber(1);
      setDebouncedFilter(filter);
    }, 350);
    return () => clearTimeout(timer);
  }, [filter]);

  useEffect(() => {
    getStudyManagementData();
  }, [statusFilter, pageNumber, itemsPerPage, debouncedFilter]);

  useEffect(() => {
    if (modalOpen === 'deletedHistory') {
      getDeletedStudyManagementData();
    }
  }, [modalOpen, deletedPageNumber, deletedItemsPerPage]);

  const mappedStudies = useMemo(() => (
    (studyList || []).map((s) => ({
      id: s.studyId || s.id,
      _id: s._id,
      studyId: s.studyId,
      studyCode: s.studyCode || '',
      studyLabel: s.studyLabel || '',
      phaseType: s.phaseType || '',
      moleculeProduct: s.moleculeProduct || '',
      design: s.design || '',
      doseForm: s.doseForm || '',
      country: s.country || '',
      code: s.studyCode || '',
      protocolTitle: s.protocolTitle || '',
      molecule: s.moleculeProduct || '',
      indication: s.indication || '',
      cro: s.cro || '—',
      phase: s.phaseType || '—',
      status: (s.status === 'hold' ? 'on_hold' : (s.status || 'active')),
      legacy: false,
      plannedStart: s.plannedStart || '',
      plannedEnd: s.plannedEnd || ''
    }))
  ), [studyList]);

  const filtered = useMemo(() => {
    return mappedStudies.filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (!filter) return true;
      const q = filter.toLowerCase();
      return (
        (s.code || '').toLowerCase().includes(q) ||
        (s.studyLabel || '').toLowerCase().includes(q) ||
        (s.protocolTitle || '').toLowerCase().includes(q) ||
        (s.molecule || '').toLowerCase().includes(q)
      );
    });
  }, [mappedStudies, filter, statusFilter]);

  const statusVariant = (s) => ({
    active: 'ok',
    planned: 'info',
    completed: 'gold',
    on_hold: 'warn',
    archived: 'default'
  })[s] || 'default';

  const openStudyDetail = (study) => {
    // Keep detail page compatible with context-based study lookup.
    upsert('studies', {
      id: study.id,
      studyId: study.studyId,
      studyCode: study.studyCode || study.code,
      studyLabel: study.studyLabel,
      code: study.code,
      phaseType: study.phaseType || study.phase,
      phase: study.phase,
      moleculeProduct: study.moleculeProduct || study.molecule,
      protocolTitle: study.protocolTitle,
      molecule: study.molecule,
      indication: study.indication,
      design: study.design,
      doseForm: study.doseForm,
      cro: study.cro,
      status: study.status,
      country: study.country,
      plannedStart: study.plannedStart,
      plannedEnd: study.plannedEnd
    });
    onOpenStudy(study.id);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Repository</div>
          <h1 className="page-title">Studies & Documents</h1>
          <p className="page-sub">Folder-based study repository with versioning, soft-delete, audit trail and role-based access.</p>
        </div>
        <div className="flex gap-2">
          <button className="btn"><i className="ti ti-download" /> Export index</button>
          <button className="btn btn-danger" onClick={() => setModalOpen('deletedHistory')}>
            <i className="ti ti-history" /> Deleted history
          </button>
          <button className="btn btn-primary" onClick={openAddModal}>
            <i className="ti ti-plus" /> New study
          </button>
        </div>
      </div>

      <MetricStrip
        items={[
          {
            label: 'Total studies',
            value: studyCount || mappedStudies.length,
            foot: `${mappedStudies.filter((s) => s.status === 'active').length} active`
          },
          {
            label: 'Planned studies',
            value: mappedStudies.filter((s) => s.status === 'planned').length,
            foot: 'ready for kickoff'
          },
          {
            label: 'Completed studies',
            value: mappedStudies.filter((s) => s.status === 'completed').length,
            foot: 'closed studies'
          },
          {
            label: 'On hold',
            value: mappedStudies.filter((s) => s.status === 'on_hold').length,
            foot: 'need intervention',
            dir: mappedStudies.some((s) => s.status === 'on_hold') ? 'warn' : 'up'
          }
        ]}
      />

      <div className="section-label mt-4 mb-5">
        All studies <span className="count">{filtered.length}</span>
      </div>

      <div className="card">
        <div className="flex gap-2 mb-3" style={{ padding: '14px 18px 0' }}>
          <div style={{ position: 'relative', flex: '0 0 260px' }}>
            <i className="ti ti-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-3)', fontSize: 13 }} />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search studies..."
              style={{ width: '100%', padding: '7px 10px 7px 30px', border: '1px solid var(--line)', borderRadius: 2, fontSize: 12 }}
            />
          </div>
          <select className="btn btn-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="planned">Planned</option>
            <option value="completed">Completed</option>
            <option value="on_hold">On hold</option>
          </select>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div className="buttonLoader" />
            <div style={{ marginTop: 12 }}>Loading studies...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 24 }}>
            <EmptyState icon="ti-folder-off" title="No studies match" desc="Adjust your filter or create a new study." />
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Study</th>
                <th>Protocol</th>
                <th>Molecule</th>
                <th>CRO</th>
                <th>Phase</th>
                <th>Status</th>
                <th>Timeline</th>
                <th style={{ width: 120 }}>Actions</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="clickable-row" onClick={() => openStudyDetail(s)}>
                  <td>
                    <div style={{ fontWeight: 500, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.studyLabel}
                    </div>
                    <div className="xs muted">{s.code}</div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.protocolTitle}
                    </div>
                    <div className="xs muted">{s.indication}</div>
                  </td>
                  <td>{s.molecule || '—'}</td>
                  <td>{s.cro}</td>
                  <td><Tag>{s.phase}</Tag></td>
                  <td><Tag variant={statusVariant(s.status)} dot>{s.status}</Tag></td>
                  <td className="xs muted">{fmtDate(s.plannedStart)} → {fmtDate(s.plannedEnd)}</td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        className="icon-btn"
                        title="Edit"
                        style={{ color: 'var(--info)' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openUpdateModal(s);
                        }}
                      >
                        <i className="ti ti-edit" />
                      </button>
                      <button
                        className="icon-btn"
                        title="Delete"
                        style={{ color: 'var(--danger)' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setStudyData(s);
                          setModalOpen('delete');
                        }}
                      >
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                  </td>
                  <td>
                    <i className="ti ti-chevron-right clickable-row-arrow" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          totalPage={totalPage || 1}
          totalItems={studyCount}
        />
      </div>

      <Modal
        open={modalOpen === 'add'}
        onClose={closeModal}
        title="New study"
        wide
        footer={
          <>
            <button className="btn" onClick={closeModal}>Cancel</button>
            <button className="btn btn-primary" onClick={addStudyManagementData} >
              <i className="ti ti-check" /> {buttonLoader ? 'Creating...' : 'Create study'}
            </button>
          </>
        }
      >
        <div className="field-row">
          <div className="field">
            <label className="field-label">Study Label</label>
            <input value={addForm?.studyLabel} onChange={(e) => setAddForm((p) => ({ ...p, studyLabel: e.target.value }))} placeholder="Enter Study Label" />
          </div>
          <div className="field">
            <label className="field-label">Study Code <span className="req">*</span></label>
            <input value={addForm?.studyCode} onChange={(e) => setAddForm((p) => ({ ...p, studyCode: e.target.value }))} placeholder="Enter Study Code" />
          </div>
        </div>

        <div className="field-row">
          <div className="field">
            <label className="field-label">Phase / Type</label>
            <select value={addForm?.phaseType} onChange={(e) => setAddForm((p) => ({ ...p, phaseType: e.target.value }))}>
              <option value="">Select Phase / Type</option>
              <option value="BABE">BABE</option>
              <option value="Phase I">Phase I</option>
              <option value="Phase II">Phase II</option>
              <option value="Phase III">Phase III</option>
              <option value="Phase IV">Phase IV</option>
            </select>
          </div>
          <div className="field">
            <label className="field-label">Protocol title <span className="req">*</span></label>
            <input value={addForm?.protocolTitle} onChange={(e) => setAddForm((p) => ({ ...p, protocolTitle: e.target.value }))} placeholder="Enter Protocol title" />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label className="field-label">Molecule / product</label>
            <input value={addForm?.moleculeProduct} onChange={(e) => setAddForm((p) => ({ ...p, moleculeProduct: e.target.value }))} placeholder="Enter Molecule / Product" />
          </div>
          <div className="field">
            <label className="field-label">Indication</label>
            <input value={addForm?.indication} onChange={(e) => setAddForm((p) => ({ ...p, indication: e.target.value }))} placeholder="Enter Indication" />
          </div>
        </div>

        <div className="field-row-3">
          <div className="field">
            <label className="field-label">Design</label>
            <input value={addForm?.design} onChange={(e) => setAddForm((p) => ({ ...p, design: e.target.value }))} placeholder="Enter Design" />
          </div>
          <div className="field">
            <label className="field-label">Dosage Form</label>
            <input value={addForm?.doseForm} onChange={(e) => setAddForm((p) => ({ ...p, doseForm: e.target.value }))} placeholder="Enter Dosage Form" />
          </div>
          <div className="field">
            <label className="field-label">CRO</label>
            <input value={addForm?.cro} onChange={(e) => setAddForm((p) => ({ ...p, cro: e.target.value }))} placeholder="Enter CRO" />
          </div>
        </div>

        <div className="field-row-3">
          <div className="field">
            <label className="field-label">Country</label>
            <select
              className="inp"
              disabled={buttonLoader || countryList?.length === 0}
              value={addForm?.country}
              onChange={(e) => setAddForm((p) => ({ ...p, country: e.target.value }))}
            >
              <option value="">Select Country</option>
              {countryList?.map((item) => (
                <option key={item.name} value={item.name}>{item.name}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field-label">Planned Start</label>
            <input type="date" value={addForm?.plannedStart} onChange={(e) => setAddForm((p) => ({ ...p, plannedStart: e.target.value }))} placeholder="Select Planned Start" />
          </div>
          <div className="field">
            <label className="field-label">Planned End</label>
            <input type="date" value={addForm?.plannedEnd} onChange={(e) => setAddForm((p) => ({ ...p, plannedEnd: e.target.value }))} placeholder="Select Planned End" />
          </div>
        </div>
      </Modal>

      <Modal
        open={modalOpen === 'update'}
        onClose={closeModal}
        title="Update study"
        wide
        footer={
          confirmUpdate ? (
            <>
              <button className="btn" onClick={() => setConfirmUpdate(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={updateStudyManagementData} disabled={buttonLoader || !updateForm.studyCode || !updateForm.protocolTitle}>
                <i className="ti ti-check" /> {buttonLoader ? 'Updating...' : 'Yes, Update It!'}
              </button>
            </>
          ) : (
            <>
              <button className="btn" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={() => setConfirmUpdate(true)} disabled={buttonLoader || !updateForm.studyCode || !updateForm.protocolTitle}>
                Update Study
              </button>
            </>
          )
        }
      >
        <div className="field-row">
          <div className="field">
            <label className="field-label">Study Label</label>
            <input value={updateForm.studyLabel} onChange={(e) => setUpdateForm((p) => ({ ...p, studyLabel: e.target.value }))} placeholder="Enter Study Label" />
          </div>
          <div className="field">
            <label className="field-label">Study Code <span className="req">*</span></label>
            <input value={updateForm.studyCode} onChange={(e) => setUpdateForm((p) => ({ ...p, studyCode: e.target.value }))} placeholder="Enter Study Code" />
          </div>
        </div>

        <div className="field-row">
          <div className="field">
            <label className="field-label">Phase / Type</label>
            <select value={updateForm.phaseType} onChange={(e) => setUpdateForm((p) => ({ ...p, phaseType: e.target.value }))}>
              <option value="">Select Phase / Type</option>
              <option value="BABE">BABE</option>
              <option value="Phase I">Phase I</option>
              <option value="Phase II">Phase II</option>
              <option value="Phase III">Phase III</option>
              <option value="Phase IV">Phase IV</option>
            </select>
          </div>
          <div className="field">
            <label className="field-label">Protocol Title <span className="req">*</span></label>
            <input value={updateForm.protocolTitle} onChange={(e) => setUpdateForm((p) => ({ ...p, protocolTitle: e.target.value }))} placeholder="Enter Protocol title" />
          </div>
        </div>


        <div className="field-row">
          <div className="field">
            <label className="field-label">Molecule / Product</label>
            <input value={updateForm.moleculeProduct} onChange={(e) => setUpdateForm((p) => ({ ...p, moleculeProduct: e.target.value }))} placeholder="Enter Molecule / Product" />
          </div>
          <div className="field">
            <label className="field-label">Indication</label>
            <input value={updateForm.indication} onChange={(e) => setUpdateForm((p) => ({ ...p, indication: e.target.value }))} placeholder="Enter Indication" />
          </div>
        </div>

        <div className="field-row-3">
          <div className="field">
            <label className="field-label">Design</label>
            <input value={updateForm.design} onChange={(e) => setUpdateForm((p) => ({ ...p, design: e.target.value }))} placeholder="Enter Design" />
          </div>
          <div className="field">
            <label className="field-label">Dosage form</label>
            <input value={updateForm.doseForm} onChange={(e) => setUpdateForm((p) => ({ ...p, doseForm: e.target.value }))} placeholder="Enter Dosage Form" />
          </div>
          <div className="field">
            <label className="field-label">CRO</label>
            <input value={updateForm.cro} onChange={(e) => setUpdateForm((p) => ({ ...p, cro: e.target.value }))} placeholder="Enter CRO" />
          </div>
        </div>

        <div className="field-row-3">
          <div className="field">
            <label className="field-label">Country</label>
            <select
              className="inp"
              disabled={buttonLoader || countryList?.length === 0}
              value={updateForm?.country}
              onChange={(e) => setUpdateForm((p) => ({ ...p, country: e.target.value }))}
            >
              <option value="">Select Country</option>
              {countryList?.map((item) => (
                <option key={item.name} value={item.name}>{item.name}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field-label">Planned Start</label>
            <input type="date" value={updateForm.plannedStart} onChange={(e) => setUpdateForm((p) => ({ ...p, plannedStart: e.target.value }))} placeholder="Select Planned Start" />
          </div>
          <div className="field">
            <label className="field-label">Planned End</label>
            <input type="date" value={updateForm.plannedEnd} onChange={(e) => setUpdateForm((p) => ({ ...p, plannedEnd: e.target.value }))} placeholder="Select Planned End" />
          </div>
        </div>
      </Modal>

      <Modal
        open={modalOpen === 'delete'}
        onClose={closeModal}
        title="Delete study"
        footer={
          <>
            <button className="btn" onClick={closeModal}>Cancel</button>
            <button className="btn btn-danger" onClick={softDeleteStudyManagement} disabled={buttonLoader}>
              {buttonLoader ? 'Deleting...' : 'Yes, Delete It!'}
            </button>
          </>
        }
      >
        <p className="xs muted">Move this study to deleted history.</p>
      </Modal>

      <Modal
        open={modalOpen === 'deletedHistory'}
        onClose={closeModal}
        title="Deleted Study List"
        wide
      >
        <table className="tbl">
          <thead>
            <tr>
              <th>Study code</th>
              <th>Protocol</th>
              <th>Status</th>
              <th style={{ width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {deletedStudyList.map((item) => (
              <tr key={item._id || item.studyId}>
                <td>{item.studyCode || '—'}</td>
                <td>{item.protocolTitle || '—'}</td>
                <td><Tag variant="default">deleted</Tag></td>
                <td>
                  <div className="flex gap-2">
                    <button
                      className="icon-btn"
                      title="Restore"
                      style={{ color: 'var(--info)' }}
                      onClick={() => restoreStudyManagement(item._id, item.studyId)}
                    >
                      <i className="ti ti-rotate" />
                    </button>
                    <button
                      className="icon-btn"
                      title="Permanent Delete"
                      style={{ color: 'var(--danger)' }}
                      onClick={() => deleteStudyManagementData(item._id, item.studyId)}
                    >
                      <i className="ti ti-trash" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {deletedStudyList.length === 0 && (
              <tr>
                <td colSpan={4}>
                  <EmptyState icon="ti-history-off" title="No deleted studies" desc="Deleted studies will appear here." />
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Pagination
          itemsPerPage={deletedItemsPerPage}
          setItemsPerPage={setDeletedItemsPerPage}
          pageNumber={deletedPageNumber}
          setPageNumber={setDeletedPageNumber}
          totalPage={deletedTotalPage || 1}
          totalItems={deletedStudyCount}
        />
      </Modal>

      <style>{`
        .clickable-row {
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .clickable-row:hover {
          background: var(--surface-2);
        }

        .clickable-row-arrow {
          color: var(--ink-3);
          font-size: 14px;
          opacity: 0.75;
        }

        .clickable-row:hover .clickable-row-arrow {
          opacity: 1;
          color: var(--ink);
        }
      `}</style>
    </div>
  );
}

export default StudiesListPage;